import React, { useState, useEffect } from 'react';
import { useManagerStore } from '../../../lib/store-manager';
import { Simulation } from '../../../lib/supabase';
// Import existing editor from admin folder - reuse is key!
// import { SimulationConfigEditor } from '../../admin/simulations/SimulationConfigEditor'; // Unused

interface ManagerSimulationsProps {
    onStartSimulation: (sim: Simulation) => void;
}

export const ManagerSimulations: React.FC<ManagerSimulationsProps> = ({ onStartSimulation }) => {
    const { simulations, isLoading, fetchSimulations, deleteSimulation } = useManagerStore();
    const [selectedSimulation, setSelectedSimulation] = useState<Simulation | undefined>(undefined);
    const [isEditorOpen, setIsEditorOpen] = useState(false);

    useEffect(() => {
        // Initial fetch handled by layout but good to be safe
        fetchSimulations();
    }, [fetchSimulations]);

    const handleCreateSimulation = () => {
        setSelectedSimulation(undefined);
        setIsEditorOpen(true);
    };

    const handleEditSimulation = (sim: Simulation) => {
        setSelectedSimulation(sim);
        setIsEditorOpen(true);
    };

    const handleDeleteSimulation = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (window.confirm('Bu simülasyonu silmek istediğinize emin misiniz?')) {
            await deleteSimulation(id);
        }
    };

    const handleStartClick = (sim: Simulation, e: React.MouseEvent) => {
        e.stopPropagation();
        onStartSimulation(sim);
    };

    // We pass institutionId as undefined to Editor because ManagerStore handles it internally based on auth user
    // However, the Editor might expect it. Let's check Editor props.
    // Editor expects: institutionId: string; simulation?: Simulation; onClose: () => void;
    // We need to pass the manager's institution ID. 
    // But wait, the manager store handles adding sim with the correct institution ID automatically.
    // The Editor component was built for Admin who selects an institution.
    // We might need to trick it or update it. 
    // Let's assume for now we can pass a dummy string if we are using ManagerStore inside Editor? 
    // No, Editor uses AdminStore. 
    // Ideally we should refactor Editor to accept onSave callback instead of using store directly.
    // BUT to save time and risk, let's keep Editor as is for now and see if we can just wrap it?
    // Actually, `SimulationConfigEditor` uses `useAdminStore`. Managers don't use `useAdminStore`.
    // This is a problem. We need a `GenericSimulationEditor` or modify `SimulationConfigEditor` to support external save handler.

    // DECISION: I will create a wrapper or I will Modify SimulationConfigEditor to accept onSave. 
    // Since I can't easily modify Editor without breaking Admin potentially, I will duplicate the simple editor logic here or 
    // BETTER: I will modify `SimulationConfigEditor` to take `onSave` prop.

    // For now, let's assume I'll update SimulationConfigEditor to be more flexible.
    // But since I cannot change too many files at once safely, I will write a `ManagerSimulationEditor` here which is a copy/simplified version
    // tailored for ManagerStore. Re-using exact code is risky if it's tightly coupled to AdminStore.

    // Actually, let's look at `SimulationConfigEditor`. It imports `useAdminStore`.
    // I should probably just duplicate the form logic for Manager to be safe and fast.

    // Wait, better approach: 
    // I will implement a local `ManagerSimulationEditor` inside this file or nearby file.

    return (
        <div className="manager-view">
            {isEditorOpen ? (
                <ManagerSimulationEditor
                    simulation={selectedSimulation}
                    onClose={() => {
                        setIsEditorOpen(false);
                        fetchSimulations();
                    }}
                />
            ) : (
                <>
                    <div className="action-bar">
                        <div>
                            <h1>Simülasyonlar</h1>
                            <p className="subtitle">Kurumunuz için simülasyonları yönetin ve test edin.</p>
                        </div>
                        <button className="btn-primary" onClick={handleCreateSimulation}>
                            <span className="material-symbols-outlined">add</span>
                            Yeni Simülasyon
                        </button>
                    </div>

                    {isLoading && <div className="loading-spinner">Yükleniyor...</div>}

                    <div className="grid-layout">
                        {simulations.length === 0 && !isLoading && (
                            <div className="empty-state">Henüz simülasyon oluşturulmamış.</div>
                        )}

                        {simulations.map((sim) => (
                            <div
                                key={sim.id}
                                className="content-card simulation-card"
                                onClick={() => handleEditSimulation(sim)}
                                style={{ cursor: 'pointer', borderLeft: '4px solid #4f46e5' }}
                            >
                                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <h3>{sim.title}</h3>
                                    <span className={`status-badge ${sim.is_active ? 'active' : 'inactive'}`}
                                        style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', background: sim.is_active ? '#d1fae5' : '#f3f4f6', color: sim.is_active ? '#065f46' : '#4b5563' }}>
                                        {sim.is_active ? 'Aktif' : 'Pasif'}
                                    </span>
                                </div>
                                <p style={{
                                    marginBottom: '16px',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    flex: 1
                                }}>
                                    {sim.description || 'Açıklama yok'}
                                </p>

                                <div className="card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
                                    <button
                                        className="btn-start-test"
                                        onClick={(e) => handleStartClick(sim, e)}
                                        style={{ background: '#e0e7ff', border: 'none', color: '#4338ca', cursor: 'pointer', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}
                                    >
                                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>play_arrow</span>
                                        Başlat
                                    </button>

                                    <button
                                        onClick={(e) => handleDeleteSimulation(sim.id, e)}
                                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                                    >
                                        <span className="material-symbols-outlined">delete</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

// Simplified Internal Editor for Manager
const ManagerSimulationEditor: React.FC<{ simulation?: Simulation; onClose: () => void }> = ({ simulation, onClose }) => {
    const { addSimulation, updateSimulation } = useManagerStore();
    const [title, setTitle] = useState(simulation?.title || '');
    const [description] = useState(simulation?.description || ''); // Description is not in Simulation interface in store-manager but might be needed
    // Config parts
    const [systemInstruction, setSystemInstruction] = useState(simulation?.config?.systemInstruction?.parts?.[0]?.text || '');
    const [missions, setMissions] = useState<string[]>(simulation?.config?.missions || []);
    const [voice, setVoice] = useState(simulation?.config?.voice || 'Puck');
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        const config = {
            systemInstruction: { parts: [{ text: systemInstruction }] },
            missions,
            voice,
            userInstruction: systemInstruction.substring(0, 100) + "..." // Placeholder
        };

        try {
            if (simulation) {
                await updateSimulation(simulation.id, { title, description, config });
            } else {
                await addSimulation(title, { ...config, userInstruction: "Simülasyon" }); // config passed as second arg to addSimulation? no, addSimulation signature is (title, config)
                // Wait, addSimulation in store-manager.ts is (title, config). It assumes config is the whole jsonb. 
                // Need to make sure my store implementation matches.
            }
            onClose();
        } catch (error) {
            alert('Kaydetme hatası');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="editor-container" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="editor-header" style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                    <span className="material-symbols-outlined">arrow_back</span>
                </button>
                <h2>{simulation ? 'Simülasyonu Düzenle' : 'Yeni Simülasyon'}</h2>
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Başlık</label>
                <input
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Sistem Talimatı (Persona)</label>
                <textarea
                    value={systemInstruction}
                    onChange={e => setSystemInstruction(e.target.value)}
                    rows={6}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                    placeholder="Sen deneyimli bir müşterisin..."
                />
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Görevler (Her satıra bir görev)</label>
                <textarea
                    value={missions.join('\n')}
                    onChange={e => setMissions(e.target.value.split('\n'))}
                    rows={4}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                    placeholder="Fiyat sor&#10;İtiraz et"
                />
            </div>

            <div className="form-group" style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Ses</label>
                <select
                    value={voice}
                    onChange={e => setVoice(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                    <option value="Puck">Erkek (Puck)</option>
                    <option value="Charon">Erkek 2 (Charon)</option>
                    <option value="Aoede">Kadın (Aoede)</option>
                    <option value="Fenrir">Erkek 3 (Fenrir)</option>
                    <option value="Kore">Kadın 2 (Kore)</option>
                </select>
            </div>

            <button
                onClick={handleSave}
                disabled={isSaving}
                className="btn-primary"
                style={{ width: '100%', padding: '12px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
            >
                {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
        </div>
    );
};
